import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, Lock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, money, logoUrl } from "@/lib/api";

export default function DepositPage() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const [dep, setDep] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const captured = useRef(false);
  const load = () => api.get(`/public/deposits/${token}`).then((r) => setDep(r.data)).catch(() => setError("Deposit not found"));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const orderId = params.get("token");
    if (!orderId || params.get("cancelled") || captured.current) return;
    captured.current = true;
    setBusy(true);
    api.post(`/public/deposits/${token}/paypal/capture`, { order_id: orderId })
      .then(() => { setParams({}, { replace: true }); return load(); })
      .catch((e) => setError(errMsg(e, "Payment could not be completed"))).finally(() => setBusy(false));
  }, [params, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async () => {
    setBusy(true); setError("");
    try {
      const base = `${window.location.origin}/deposit/${token}`;
      const r = await api.post(`/public/deposits/${token}/paypal/order`, { return_url: base, cancel_url: `${base}?cancelled=1` });
      window.location.href = r.data.approve_url;
    } catch (e) { setError(errMsg(e, "Could not start PayPal checkout")); setBusy(false); }
  };

  if (!dep && !error) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>;
  if (!dep) return <div className="min-h-screen grid place-items-center text-slate-400" data-testid="deposit-not-found">{error}</div>;
  const paid = dep.status === "paid";
  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 py-12 px-4" data-testid="deposit-page">
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="p-8 border-b border-slate-200 flex items-center justify-between gap-4">
          {dep.has_logo ? <img src={logoUrl("deposit")} alt={dep.company_name} className="max-h-12 max-w-[180px] object-contain" /> : <span className="font-display text-xl font-bold">{dep.company_name}</span>}
          <p className="text-xs uppercase tracking-wider text-slate-500">Booking deposit</p>
        </div>
        <div className="p-8 space-y-6">
          <div className="text-sm"><p className="text-xs uppercase tracking-wider text-slate-500 mb-1">For</p><p className="font-semibold">{dep.name}</p>{dep.company && <p className="text-slate-500">{dep.company}</p>}</div>
          <div className="rounded-xl border border-slate-200">
            <div className="flex justify-between p-4 text-sm"><span>{dep.tier_label} study</span><span className="num">{money(dep.price, dep.currency)}</span></div>
            <div className="flex justify-between p-4 border-t border-slate-200 bg-slate-50 rounded-b-xl"><span className="font-semibold">Deposit due now ({dep.percent}%)</span><span className="num text-xl font-bold" data-testid="deposit-amount">{money(dep.amount, dep.currency)}</span></div>
          </div>
          <p className="text-xs text-slate-500">The deposit is credited in full on your final invoice.</p>
          {error && <p className="flex items-center gap-2 text-sm text-rose-600" data-testid="deposit-error"><AlertTriangle className="h-4 w-4" />{error}</p>}
          {params.get("cancelled") && !paid && <p className="text-sm text-amber-600" data-testid="deposit-cancelled">Payment was cancelled. You can try again.</p>}
          {paid ? <div className="flex items-center gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-emerald-800" data-testid="deposit-success"><CheckCircle2 className="h-5 w-5" />Deposit paid{dep.paid_at ? ` on ${dep.paid_at.slice(0, 10)}` : ""}. We'll be in touch shortly. Thank you!</div>
            : dep.paypal_enabled ? <Button data-testid="deposit-paypal-btn" disabled={busy} onClick={pay} className="w-full h-12 rounded-full bg-[#ffc439] hover:bg-[#f2b92f] text-[#003087] font-bold text-base">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Pay with <span className="italic ml-1">PayPal</span></>}</Button>
              : <p className="text-sm text-slate-500">Online payment is not available right now. We'll contact you shortly.</p>}
          {!paid && dep.paypal_enabled && <p className="flex items-center justify-center gap-1 text-xs text-slate-400"><Lock className="h-3 w-3" />Secure checkout by PayPal — cards accepted, no PayPal account needed</p>}
        </div>
      </div>
    </div>
  );
}
